import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import {
	SEX_LIST,
	YESNO,
	YESNOLIST,
} from '@/common/constants/common';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import { useGetAllEnumListQuery, useGetDicListQuery } from '@/api/common';
import SingleCheckbox from '@/common/components/form-inputs/SingleCheckbox';
import AppMultipleDropDownMenu from '@/common/components/form-inputs/AppMultipleDropDownMenu';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
	allEnumList?: any;
}

const EditEducationWorkLivingStatusForm = forwardRef(
	({ type, setType, details, allEnumList }: Props, ref) => {
		const { t } = useTranslation();
		const defaultValues: DefaultValues<any> = {};

		const { data: financialConditionDicList } = useGetDicListQuery(
			'financial_condition',
			{ refetchOnMountOrArgChange: true }
		);
		const { data: workStatusDicList } = useGetDicListQuery('work_status', {
			refetchOnMountOrArgChange: true,
		});
		const { data: educationDicList } = useGetDicListQuery('education', {
			refetchOnMountOrArgChange: true,
		});
		const { data: livingStatusDicList } = useGetDicListQuery('living_status', {
			refetchOnMountOrArgChange: true,
		});
		const { data: familySupportDegreeDicList } = useGetDicListQuery(
			'degree_of_family_support',
			{ refetchOnMountOrArgChange: true }
		);

		useEffect(() => {
			if (details) {
				methods.reset(details);
				methods.setValue(
					'attendingOtherYouthService',
					details.attendingOtherYouthService == null
						? null
						: !!details.attendingOtherYouthService
				);
				methods.setValue(
					'studyingSecondarySchool',
					details.studyingSecondarySchool == null
						? null
						: !!details.studyingSecondarySchool
				);
			}
		}, [details]);

		const formSchema = Yup.object().shape({
			chiFullNameOfSecondarySchool: Yup.string().when(
				'studyingSecondarySchool',
				{
					is: (studyingSecondarySchool: string) => studyingSecondarySchool,
					then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
					otherwise: (schema) => schema.nullable(),
				}
			),
			nameOfOtherYouthService: Yup.string().when('attendingOtherYouthService', {
				is: (attendingOtherYouthService: string) => attendingOtherYouthService,
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),
			nameOfResidentialCareHome: Yup.string().when('livingStatus', {
				is: (livingStatus: string) => livingStatus && livingStatusIsOther(),
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		const livingStatusIsOther = () => {
			const obj = livingStatusDicList?.data.find(
				(item: any) => item.id == watchLivingStatus
			);
			if (obj && obj.labelEn === 'Others') return true;
			return false;
		};
		const watchLivingStatus = methods.watch('livingStatus');
		const watchStudyingSecondarySchool = methods.watch(
			'studyingSecondarySchool'
		);
		const watchAttendingOtherYouthService = methods.watch(
			'attendingOtherYouthService'
		);

		useImperativeHandle(ref, () => ({
			getFormData: () => {
				// The optional Yes/No dropdowns emit '' for "None"; send null so it is not stored as No.
				const values = methods.getValues();
				const yesNoOrNull = (value: unknown) =>
					value === '' || value == null ? null : !!value;
				return {
					...values,
					studyingSecondarySchool: yesNoOrNull(values.studyingSecondarySchool),
					attendingOtherYouthService: yesNoOrNull(
						values.attendingOtherYouthService
					),
				};
			},
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.educationWorkLivingStatus')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppMultipleDropDownMenu
									control={methods.control}
									name='financialConditionList'
									label={t('preAdmission.financialCondition')}
									options={(financialConditionDicList?.data || []).map(
										(item: { labelEn: any }) => {
											return {
												...item,
												name: item.labelEn,
											};
										}
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='workStatus'
									label={t('preAdmission.workStatus')}
									options={(workStatusDicList?.data || []).map(
										(item: { labelEn: any }) => {
											return {
												...item,
												name: item.labelEn,
											};
										}
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='workMotivation'
									label={t('preAdmission.workMotivation')}
									options={(allEnumList?.data.WorkMotivationEnum || []).map(
										(item: any) => {
											return {
												...item,
												id: item.code,
												name: item.desc,
											};
										}
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='education'
									label={t('preAdmission.education')}
									options={(educationDicList?.data || []).map(
										(item: { labelEn: any }) => {
											return {
												...item,
												name: item.labelEn,
											};
										}
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='studyingSecondarySchool'
									label={t('preAdmission.studyingSecondarySchool')}
									options={YESNO}
									handleChange={() => {
										methods.setValue('chiFullNameOfSecondarySchool', '');
										methods.clearErrors('chiFullNameOfSecondarySchool');
									}}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									disabled={!watchStudyingSecondarySchool}
									control={methods.control}
									name='chiFullNameOfSecondarySchool'
									label={`${t('preAdmission.studyingSecondarySchoolSpecify')}${
										watchStudyingSecondarySchool ? '*' : ''
									}`}
								/>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='attendingOtherYouthService'
									label={t('preAdmission.attendingOtherYouthService')}
									options={YESNO}
									handleChange={() => {
										methods.setValue('nameOfOtherYouthService', '');
										methods.clearErrors('nameOfOtherYouthService');
									}}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									disabled={!watchAttendingOtherYouthService}
									control={methods.control}
									name='nameOfOtherYouthService'
									label={`${t(
										'preAdmission.attendingOtherYouthServiceSpecify'
									)}${watchAttendingOtherYouthService ? '*' : ''}`}
								/>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='livingStatus'
									label={t('preAdmission.livingStatus')}
									options={(livingStatusDicList?.data || []).map(
										(item: { labelEn: any }) => {
											return {
												...item,
												name: item.labelEn,
											};
										}
									)}
									handleChange={() => {
										methods.setValue('nameOfResidentialCareHome', '');
										methods.clearErrors('nameOfResidentialCareHome');
									}}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									disabled={!livingStatusIsOther()}
									control={methods.control}
									name='nameOfResidentialCareHome'
									label={`${t('preAdmission.livingStatusSpecify')}${
										livingStatusIsOther() ? '*' : ''
									}`}
								/>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='livingWithPersonsAgedUnder18'
									label={t('preAdmission.livingWithPersonAgedUnder18')}
									options={(
										allEnumList?.data.LivingWithPersonsAgedUnder18Enum || []
									).map((item: any) => {
										return {
											...item,
											id: item.code,
											name: item.desc,
										};
									})}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='noOfPeopleLivingTogether'
									label={t('preAdmission.excludePrincipalClient')}
								/>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='degreeOfFamilySupport'
									label={t('preAdmission.degreeFamilySupport')}
									options={(familySupportDegreeDicList?.data || []).map(
										(item: { labelEn: any }) => {
											return {
												...item,
												name: item.labelEn,
											};
										}
									)}
								/>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
			</>
		);
	}
);

EditEducationWorkLivingStatusForm.displayName =
	'EditEducationWorkLivingStatusForm';

export default EditEducationWorkLivingStatusForm;
